import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, Car } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const NotFoundContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const NotFoundContent = styled.div`
  text-align: center;
  max-width: 600px;
  width: 100%;
`;

const ErrorNumber = styled(motion.div)`
  font-size: 8rem;
  font-weight: 900;
  background: linear-gradient(45deg, #F43F60, #FF6B6B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 4rem;
  }
`;

const ErrorTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 2rem 0;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const IconContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
  
  .car-icon {
    font-size: 8rem;
    color: #F43F60;
    opacity: 0.8;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const LinkButton = styled(Link)`
  background: ${props => props.primary ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
    color: white;
    text-decoration: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const SearchSuggestion = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
    color: #F43F60;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 1rem 0;
    line-height: 1.6;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
  }
  
  li {
    background: rgba(244, 63, 96, 0.1);
    border: 1px solid rgba(244, 63, 96, 0.3);
    border-radius: 20px;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    color: #F43F60;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(244, 63, 96, 0.2);
      transform: translateY(-1px);
    }
  }
`;

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSearchCars = () => {
    navigate('/');
  };

  const popularSearches = [
    'BMW X5',
    'Toyota Camry',
    'Honda Civic',
    'Ford F-150',
    'Tesla Model 3',
    'Mercedes C-Class'
  ];

  return (
    <NotFoundContainer>
      <NotFoundContent>
        <ErrorNumber
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
        >
          404
        </ErrorNumber>
        
        <ErrorTitle
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Page Not Found
        </ErrorTitle>
        
        <ErrorMessage
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          Oops! The page you're looking for doesn't exist. 
          It might have been moved, deleted, or you entered the wrong URL.
        </ErrorMessage>
        
        <IconContainer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <Car className="car-icon" size={50} />
        </IconContainer>
        
        <ButtonContainer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <ActionButton onClick={handleGoBack}>
            <ArrowLeft size={20} />
            Go Back
          </ActionButton>
          
          <LinkButton to="/" primary>
            <Home size={20} />
            Go Home
          </LinkButton>
          
        </ButtonContainer>
        
     
      </NotFoundContent>
    </NotFoundContainer>
  );
};

export default NotFound; 